import { useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';

/**
 * Lighten a hex colour by mixing it toward white.
 * amount 0 = original, 1 = white.
 */
function lightenColor(hex, amount) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.round(((num >> 16) & 0xff) + (255 - ((num >> 16) & 0xff)) * amount);
  const g = Math.round(((num >> 8) & 0xff) + (255 - ((num >> 8) & 0xff)) * amount);
  const b = Math.round((num & 0xff) + (255 - (num & 0xff)) * amount);
  return `rgb(${r},${g},${b})`;
}

/**
 * RegionalFundingChart
 *
 * Donut chart showing the W1W2 (pooled) vs W3Bilateral (non-pooled) funding
 * split for results in a specific region. Uses two shades of the region
 * brand colour: full colour for W1W2, lighter shade for W3Bilateral.
 */
const RegionalFundingChart = memo(function RegionalFundingChart({ results, color }) {
  const { chartData, exportData, total, colors } = useMemo(() => {
    if (!results || results.length === 0) {
      return { chartData: [], exportData: [], total: 0, colors: [] };
    }

    let w1w2 = 0;
    let w3 = 0;
    for (const r of results) {
      if (r.fundingType === 'W3Bilateral') {
        w3 += 1;
      } else {
        w1w2 += 1;
      }
    }

    const _total = w1w2 + w3;
    const _chartData = [
      { name: 'W1/W2 (Pooled)', value: w1w2 },
      { name: 'W3/Bilateral', value: w3 },
    ];

    const _exportData = _chartData.map(d => ({
      'Funding Type': d.name,
      'Result Count': d.value,
      Percentage: _total > 0 ? `${((d.value / _total) * 100).toFixed(1)}%` : '0%',
    }));

    const _colors = [color, lightenColor(color, 0.5)];

    return { chartData: _chartData, exportData: _exportData, total: _total, colors: _colors };
  }, [results, color]);

  if (!chartData || chartData.length === 0 || total === 0) {
    return (
      <ChartWrapper title="Funding Split">
        <NoChartData
          message="No data available"
          detail="No funding data found for results in this region."
        />
      </ChartWrapper>
    );
  }

  const renderLabel = ({ cx, cy, midAngle, outerRadius, percent, index }) => {
    if (percent < 0.03) return null;
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 18;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        fill={colors[index] || '#374151'}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={13}
        fontWeight={600}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <ChartWrapper
      title="Funding Split"
      info="Distribution of results by funding type: W1/W2 (pooled CGIAR funding) vs W3/Bilateral (non-pooled grants) for this region."
      chartData={exportData}
      sourceResults={results}
      exportFilename="regional-funding"
    >
      <ResponsiveContainer width="100%" height={250}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={50}
            outerRadius={80}
            paddingAngle={2}
            dataKey="value"
            label={renderLabel}
            labelLine={false}
            animationBegin={0}
            animationDuration={400}
          >
            {chartData.map((entry, index) => (
              <Cell key={entry.name} fill={colors[index % colors.length]} />
            ))}
          </Pie>
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.[0]) return null;
              const d = payload[0].payload;
              const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : 0;
              return (
                <div style={{
                  background: '#fff',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  fontSize: '13px',
                  lineHeight: 1.4,
                }}>
                  <div style={{ fontWeight: 600, color: '#1e293b' }}>{d.name}</div>
                  <div style={{ color: '#4b5563' }}>
                    {d.value.toLocaleString()} result{d.value !== 1 ? 's' : ''} ({pct}%)
                  </div>
                </div>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex justify-center gap-6 mt-2">
        {chartData.map((entry, index) => (
          <div key={entry.name} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: colors[index] }}
            />
            <span className="text-sm text-gray-600">
              {entry.name}: {entry.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    </ChartWrapper>
  );
});

RegionalFundingChart.propTypes = {
  results: PropTypes.array.isRequired,
  color: PropTypes.string.isRequired,
};

export default RegionalFundingChart;
